import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Tooltip, message as antdMessage } from "antd";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";

import { citationIdFromHref } from "./responseManifest";

/* Shared markdown rendering infrastructure for the AI Assistant transcript.
   Kept in its own module (instead of index.js) so that both the page
   (index.js) and the memoized markdown renderer (MemoizedMarkdown.js) can
   import the SAME module-level component types without a circular import
   (index.js -> MemoizedMarkdown.js -> index.js). Module-level component
   types also keep react-markdown from remounting these subtrees (e.g.
   CodeBlock losing its copied state) on every keystroke or stream frame. */

/* Clipboard helper used by the code-block renderer below and by the message
   copy action in index.js. Lives here so this module has no import edge back
   into index.js. */
const copyText = async (value) => {
  const text = String(value || "");
  if (!text) return;

  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textArea);
  if (!copied) throw new Error("Copy is not available.");
};

const CodeBlock = ({ children, language }) => {
  const codeRef = useRef(null);
  const copiedTimerRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    []
  );

  const handleCopy = async () => {
    try {
      await copyText(codeRef.current?.innerText || "");
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      antdMessage.error(error?.message || "Unable to copy this code.");
    }
  };

  return (
    <div
      className={`facility-ai-code-block${language ? " facility-ai-code-block--has-language" : ""}`}
    >
      {language ? <span className="facility-ai-code-language">{language}</span> : null}
      <Tooltip title="Copy code">
        <button
          type="button"
          className={`facility-ai-code-copy${copied ? " is-copied" : ""}`}
          onClick={handleCopy}
          aria-label="Copy code"
        >
          {copied ? <CheckOutlined /> : <CopyOutlined />}
        </button>
      </Tooltip>
      <pre ref={codeRef}>{children}</pre>
    </div>
  );
};

const MarkdownPre = ({ children }) => {
  const codeElement = Array.isArray(children) ? children[0] : children;
  const language =
    codeElement?.props?.className?.match?.(/language-([\w#+.-]+)/)?.[1] || "";
  return <CodeBlock language={language}>{children}</CodeBlock>;
};

/* Message-scoped citation click handler; supplied via context (by
   MessageItem) so the renderer components themselves stay module-level and
   stable. */
const MarkdownCitationContext = createContext(() => {});

const MarkdownLink = ({ href, children }) => {
  const openCitation = useContext(MarkdownCitationContext);
  const citationId = citationIdFromHref(href);
  if (citationId) {
    return (
      <button
        type="button"
        className="facility-ai-citation-marker"
        onClick={() => openCitation(citationId)}
      >
        {children}
      </button>
    );
  }
  if (/^https?:\/\//i.test(href || "")) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return <span>{children}</span>;
};

/* Markdown <img> (3.7): lazy + async-decoded so off-screen images never
   block a streaming frame, and a broken image hides itself instead of
   showing the browser's broken-image glyph (same onError fallback idea as
   WebSearchImage in MessageItem.js). react-markdown 8 passes the hast
   `node` prop to custom components — it is not a DOM attribute and must
   be dropped before the spread. */
const MarkdownImage = (props) => {
  const { node, ...imageProps } = props;
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      loading="lazy"
      decoding="async"
      {...imageProps}
      onError={() => setFailed(true)}
    />
  );
};

/* Markdown <table> (P0 fix 3): the table element is preserved inside a
   horizontally scrollable div (.facility-ai-table-wrap, styled in styles.css)
   so wide tables scroll inside the message column instead of dragging the
   whole transcript sideways. react-markdown hands this renderer only the
   table's children (thead/tbody) — the <table> itself must be re-emitted,
   otherwise thead/tbody land directly inside a div (invalid DOM nesting, no
   table box) and the descendant table selectors in styles.css stop matching.
   Module-level and closure-free like the other component types, so
   react-markdown keeps a stable element type and never remounts the
   wrapper. */
const MarkdownTable = ({ children }) => (
  <div className="facility-ai-table-wrap">
    <table>{children}</table>
  </div>
);

const MARKDOWN_COMPONENTS = {
  pre: MarkdownPre,
  a: MarkdownLink,
  img: MarkdownImage,
  table: MarkdownTable,
};

export { copyText, MarkdownCitationContext, MARKDOWN_COMPONENTS };
